// ============================================================
// ContrastTag.tsx
// Thin wrapper around antd <Tag> that always renders readable text.
//
// When `background` is provided (hex or CSS var), the text colour is
// computed via WCAG luminance so it is always black or white against
// that background.  When no custom background is given it falls back
// to plain antd <Tag> behaviour (preset colours already handle contrast).
// ============================================================

import React from 'react';
import { Tag } from 'antd';
import type { TagProps } from 'antd';
import { getReadableTextColor } from '../../utils/colorUtils';

export interface ContrastTagProps extends Omit<TagProps, 'color'> {
  /**
   * Background colour for the tag.
   * Accepts hex ('#rgb', '#rrggbb'), rgb/rgba strings, or a CSS variable
   * reference such as 'var(--mg-primary)'.
   *
   * When supplied, the text colour is automatically set to black or white
   * for maximum WCAG contrast.  When omitted, antd's default tag rendering
   * is used unchanged (including built-in preset-colour support).
   */
  background?: string;
  /** Optional explicit border colour.  Defaults to `${background}40` when
   *  background is a hex string, or omitted otherwise. */
  borderColor?: string;
}

const ContrastTag: React.FC<ContrastTagProps> = ({
  background,
  borderColor,
  style,
  children,
  ...rest
}) => {
  if (!background) {
    // No custom background — fall through to plain antd Tag with no extra styles.
    return (
      <Tag style={style} {...rest}>
        {children}
      </Tag>
    );
  }

  const textColor = getReadableTextColor(background);

  // Derive a subtle border when not explicitly provided:
  // - hex string → append 40 (25 % opacity hex suffix)
  // - CSS var / other → leave undefined (no border override)
  const resolvedBorder =
    borderColor ??
    (background.startsWith('#') ? `${background}40` : undefined);

  return (
    <Tag
      style={{
        background,
        color: textColor,
        ...(resolvedBorder ? { border: `1px solid ${resolvedBorder}` } : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default ContrastTag;
